import {
  ADD_QUEST,
  CREATE_QUEST,
  CREATE_QUEST_SUCCESS,
  CREATE_QUEST_FAILURE,
  DELETE_QUEST,
  DELETE_QUEST_UNDO,
  DELETE_QUEST_SUCCESS,
  DELETE_QUEST_FAILURE,
  RENAME_QUEST,
  REQUEST_QUESTS,
  REQUEST_QUEST,
  RECEIVE_QUEST,
  NOT_RECEIVE_QUEST,
  RECEIVE_QUEST_CODE,
  SEND_QUEST,
  SYNC_QUEST_SUCCESS,
  SYNC_QUEST_FAILURE,
  SELECT_QUEST,
  COPY_QUEST,
  COPY_QUEST_SUCCESS,
  COPY_QUEST_FAILURE,
  TOGGLE_MUSEUM,
} from '../constants/ActionTypes';
import guid from '../functions/id/guid';
import convertEventsNextReceived from '../functions/quest/convertEventsNextReceived';
import { copyQuest } from './questsHelpers';

/**
 * Subreducer for quest-level CRUD, fetching, lifecycle, sync and settings.
 * Returns updated state copy, or null if action was not handled.
 * @param {Object} state - current state of quests slice
 * @param {Object} action - dispatched Redux action
 * @returns {Object|Null}
 */
export default function questCrudReducer(state, action) {
  let nextQuest;

  switch (action.type) {
    case ADD_QUEST:
    case CREATE_QUEST:
      return {
        ...state,
        isCreating: true,
      };

    case CREATE_QUEST_SUCCESS:
    case CREATE_QUEST_FAILURE:
      return {
        ...state,
        isCreating: false,
      };

    case DELETE_QUEST:
      return {
        ...state,
        isSaving: true,
        isSaved: false,
        isNotSaved: false,
      };

    case DELETE_QUEST_FAILURE:
    case DELETE_QUEST_UNDO:
      return {
        ...state,
        isSaving: false,
        isSaved: false,
        isNotSaved: true,
      };

    case DELETE_QUEST_SUCCESS:
      return {
        ...state,
        isSaving: false,
        isSaved: true,
        isNotSaved: false,
        quests: state.quests.filter((quest) => quest.id !== action.id),
      };

    case RENAME_QUEST:
      nextQuest = copyQuest(state, action.questId);
      if (nextQuest) {
        nextQuest.title = action.title;
        nextQuest.lastModifiedText = new Date().toISOString();
        if (nextQuest.copies) {
          nextQuest.copies = nextQuest.copies.map((copy) => ({
            ...copy,
            synchronized: false,
          }));
        }
      }

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };

    case REQUEST_QUESTS:
      return {
        ...state,
        isFetching: true,
      };

    case REQUEST_QUEST:
      nextQuest = copyQuest(state, action.id);
      if (nextQuest) {
        nextQuest.syncing = true;
      }
      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.id ? nextQuest : quest
        ),
        isFetching: true,
      };

    case RECEIVE_QUEST: {
      const { quests: stateQuests } = state;
      let nextQuests = [];
      const exist = stateQuests.filter((quest) => quest.id === action.quest.id)
        .length;
      const { routes } = action.quest;
      const receivedQuest = convertEventsNextReceived(action.quest);
      receivedQuest.ok = true;
      receivedQuest.syncing = false;

      const existingQuest = exist
        ? stateQuests.find((quest) => quest.id === action.quest.id)
        : null;
      const existingRoutes = existingQuest?.routes || [];
      const serverRoutes = (routes || []).map((p) => ({
        guid: guid(),
        ...p,
      }));

      if (serverRoutes.length === 0 && existingRoutes.length > 0) {
        receivedQuest.routes = existingRoutes;
      } else if (
        serverRoutes.length < existingRoutes.length &&
        existingRoutes.length > 0
      ) {
        receivedQuest.routes = existingRoutes;
      } else {
        receivedQuest.routes = serverRoutes;
      }

      if (exist) {
        nextQuests = stateQuests.map((quest) =>
          quest.id === receivedQuest.id ? receivedQuest : quest
        );
      } else {
        nextQuests = [...stateQuests, receivedQuest];
      }

      return {
        ...state,
        quests: nextQuests,
        isFetching: false,
      };
    }

    case NOT_RECEIVE_QUEST:
      nextQuest = copyQuest(state, action.id);
      if (nextQuest) {
        nextQuest.syncing = false;
        nextQuest.ok = false;
      }
      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
        isFetching: false,
      };

    case RECEIVE_QUEST_CODE:
      return {
        ...state,
        quests: state.quests.map((quest) => {
          if (quest.id !== action.id) return quest;
          return {
            ...quest,
            shownAccess: action.payload,
          };
        }),
      };

    case SEND_QUEST:
      nextQuest = copyQuest(state, action.id);
      if (nextQuest) {
        nextQuest.syncing = true;
      }
      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.id ? nextQuest : quest
        ),
        isSyncing: true,
      };

    case SYNC_QUEST_SUCCESS:
      nextQuest = copyQuest(state, action.id);
      if (nextQuest) {
        nextQuest.lastModified = action.lastModified;
        nextQuest.ok = true;
        nextQuest.syncing = false;
      }
      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.id ? nextQuest : quest
        ),
        isSyncing: false,
      };

    case SYNC_QUEST_FAILURE:
      nextQuest = copyQuest(state, action.id);
      if (nextQuest) {
        nextQuest.ok = false;
        nextQuest.syncing = false;
      }
      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.id ? nextQuest : quest
        ),
        isSyncing: false,
      };

    case SELECT_QUEST:
      return {
        ...state,
        currentQuestId: action.id,
      };

    case COPY_QUEST:
      return {
        ...state,
        isCopying: true,
      };

    case COPY_QUEST_SUCCESS:
    case COPY_QUEST_FAILURE:
      return {
        ...state,
        isCopying: false,
      };

    case TOGGLE_MUSEUM:
      nextQuest = copyQuest(state, action.questId);
      if (nextQuest) {
        nextQuest.isMuseum = action.isMuseum;
      }
      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };

    default:
      return null;
  }
}
